/**
 * Uploads with real per-file progress (spec 003). Each file gets its own
 * row keyed by a generated id, not by array position. A file of a type the
 * Service rejects is refused before it is sent; a failed upload keeps its
 * row with the server's error while the others carry on.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../../api/client';
import type { MediaItem } from '../../@types/Media';
import { ACCEPTED_UPLOAD_LABEL, isAcceptedUpload } from './mediaListing';

export interface UploadRow {
  key: string;
  name: string;
  progress: number;
  status: 'uploading' | 'done' | 'error';
  error?: string;
}

let seq = 0;
const nextKey = () => `upload-${Date.now().toString(36)}-${++seq}`;

/** How long a finished row stays visible. */
const DONE_LINGER_MS = 2000;

export function useMediaUploads(onUploaded: (item: MediaItem) => void) {
  const [rows, setRows] = useState<UploadRow[]>([]);
  const timers = useRef<number[]>([]);
  const onUploadedRef = useRef(onUploaded);
  onUploadedRef.current = onUploaded;

  useEffect(
    () => () => {
      timers.current.forEach((t) => window.clearTimeout(t));
    },
    []
  );

  const patch = useCallback((key: string, update: Partial<UploadRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...update } : r)));
  }, []);

  const dismiss = useCallback((key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key));
  }, []);

  const upload = useCallback(
    (files: FileList | File[], folderId: number | null) => {
      const list = Array.from(files);
      const added: UploadRow[] = [];
      const jobs: { key: string; file: File }[] = [];

      for (const file of list) {
        const key = nextKey();
        if (!isAcceptedUpload(file)) {
          added.push({
            key,
            name: file.name,
            progress: 0,
            status: 'error',
            error: `"${file.name}" is not accepted. Upload ${ACCEPTED_UPLOAD_LABEL} only.`,
          });
          continue;
        }
        added.push({ key, name: file.name, progress: 0, status: 'uploading' });
        jobs.push({ key, file });
      }
      setRows((prev) => [...prev, ...added]);

      return Promise.all(
        jobs.map(async ({ key, file }) => {
          try {
            const res = await apiClient.uploadMediaWithProgress(file, { folderId }, (percent) =>
              patch(key, { progress: percent })
            );
            if (res.success) {
              patch(key, { progress: 100, status: 'done' });
              onUploadedRef.current(res.data);
              timers.current.push(window.setTimeout(() => dismiss(key), DONE_LINGER_MS));
            } else {
              patch(key, { status: 'error', error: res.error || 'Upload failed' });
            }
          } catch (e) {
            patch(key, { status: 'error', error: e instanceof Error ? e.message : 'Upload failed' });
          }
        })
      );
    },
    [patch, dismiss]
  );

  return { rows, upload, dismiss };
}
